import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import BackButton from '../../../../core/design-system/components/BackButton';
import { usePreferences } from '../../../../shared/hooks/usePreferences';
import { useCategories } from '../../../../shared/hooks/useCategories';
import { useAuth } from '../../../../core/providers/AuthProvider';
import { useCases } from '../../../../core/di/useCases';
import { GEO } from '../../../../core/constants/geo';
import './styles.css';

// Nombre maximum d'activités sélectionnables
const MAX_ACTIVITIES = 3;

// États de la géolocalisation
const LOCATION_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error'
};

// Validation des coordonnées GPS
const isValidCoordinate = (value, min, max) =>
  typeof value === 'number' &&
  Number.isFinite(value) &&
  value >= min &&
  value <= max;

const FeedPreferencesPage = ({ hideHeader = false, onSubSectionChange }) => {
  const { t, i18n } = useTranslation(['settings', 'common']);
  const navigate = useNavigate();
  const { isLoading: preferencesLoading, get, set } = usePreferences();
  const { categories, loading: categoriesLoading } = useCategories();
  const { user, isLoading: authLoading, setUser } = useAuth();
  
  // États pour la distance
  const [maxDistance, setMaxDistance] = useState(GEO.DEFAULT_RADIUS_KM);
  
  // États pour les catégories
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  
  // États pour la localisation
  const [locationStatus, setLocationStatus] = useState(LOCATION_STATUS.IDLE);
  const [locationMessage, setLocationMessage] = useState(null);
  
  // Ref pour cleanup des timeouts
  const locationTimeoutRef = useRef(null);

  // Charger les préférences
  useEffect(() => {
    const savedDistance = get('maxDistance');
    if (savedDistance) {
      const clampedDistance = Math.min(savedDistance, GEO.MAX_RADIUS_KM);
      setMaxDistance(clampedDistance);
      if (clampedDistance !== savedDistance) {
        set('maxDistance', clampedDistance);
      }
    }
  }, [get, set]);

  // Charger les activités sauvegardées depuis le profil utilisateur
  useEffect(() => {
    if (user) {
      const interests = user.interests || [];
      setSelectedActivities(interests);
    } else {
      setSelectedActivities([]);
    }
  }, [user]);

  // Notify parent when subsection changes
  useEffect(() => {
    if (onSubSectionChange) {
      onSubSectionChange(false); // Pas de sous-section dans cette page
    }
  }, [onSubSectionChange]);

  const handleBack = () => {
    if (!hideHeader) {
      navigate(-1);
    }
  };

  // Récupérer la position actuelle depuis les préférences
  const currentPosition = get('currentPosition');
  
  // Fonction pour formater l'affichage de la position
  const getLocationDisplay = useCallback(() => {
    if (!currentPosition) return null;
    
    // Si on a une ville/pays stockée, l'afficher
    if (currentPosition.city || currentPosition.country) {
      const parts = [currentPosition.city, currentPosition.country].filter(Boolean);
      return parts.join(', ');
    }
    
    // Sinon afficher les coordonnées arrondies
    if (currentPosition.latitude && currentPosition.longitude) {
      return `${currentPosition.latitude.toFixed(4)}, ${currentPosition.longitude.toFixed(4)}`;
    }
    
    return null;
  }, [currentPosition]);

  // Cleanup du timeout au démontage
  useEffect(() => {
    return () => {
      if (locationTimeoutRef.current) {
        clearTimeout(locationTimeoutRef.current);
      }
    };
  }, []);

  // Fonction pour mettre à jour la position via GPS
  const handleUpdateLocation = useCallback(async () => {
    // Clear tout timeout précédent
    if (locationTimeoutRef.current) {
      clearTimeout(locationTimeoutRef.current);
      locationTimeoutRef.current = null;
    }

    if (!navigator.geolocation) {
      setLocationStatus(LOCATION_STATUS.ERROR);
      setLocationMessage(t('pages.feed.location.error'));
      locationTimeoutRef.current = setTimeout(() => {
        setLocationStatus(LOCATION_STATUS.IDLE);
        setLocationMessage(null);
      }, 3000);
      return;
    }

    setLocationStatus(LOCATION_STATUS.LOADING);
    setLocationMessage(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        // Tenter de récupérer la ville/pays via reverse geocoding (optionnel)
        let city = null;
        let country = null;
        
        // Valider les coordonnées avant l'appel API
        if (
          isValidCoordinate(latitude, -90, 90) &&
          isValidCoordinate(longitude, -180, 180)
        ) {
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10&addressdetails=1`,
              {
                headers: {
                  'Accept-Language': i18n.language,
                  'User-Agent': 'Raskmote-Web/1.0 (https://raskmote.app)'
                }
              }
            );
            if (response.ok) {
              const data = await response.json();
              city = data.address?.city || data.address?.town || data.address?.village || data.address?.municipality;
              country = data.address?.country;
            }
          } catch (error) {
            // Le reverse geocoding a échoué, on continue sans la ville
            console.warn('Reverse geocoding failed:', error);
          }
        } else {
          console.warn('Invalid coordinates received from geolocation, skipping reverse geocoding.');
        }
        
        // Sauvegarder la position
        const newPosition = {
          latitude,
          longitude,
          city,
          country,
          updatedAt: new Date().toISOString()
        };
        
        const saveSuccess = await set('currentPosition', newPosition);
        
        if (saveSuccess) {
          setLocationStatus(LOCATION_STATUS.SUCCESS);
          setLocationMessage(t('pages.feed.location.success'));
        } else {
          setLocationStatus(LOCATION_STATUS.ERROR);
          setLocationMessage(t('pages.feed.location.error'));
        }
        
        // Reset du message après 3 secondes
        locationTimeoutRef.current = setTimeout(() => {
          setLocationStatus(LOCATION_STATUS.IDLE);
          setLocationMessage(null);
        }, 3000);
      },
      (error) => {
        console.warn('Geolocation error:', error.code, error.message);
        setLocationStatus(LOCATION_STATUS.ERROR);
        
        if (error.code === 1) {
          setLocationMessage(t('pages.feed.location.permissionDenied'));
        } else {
          setLocationMessage(t('pages.feed.location.error'));
        }
        
        // Reset du message après 3 secondes
        locationTimeoutRef.current = setTimeout(() => {
          setLocationStatus(LOCATION_STATUS.IDLE);
          setLocationMessage(null);
        }, 3000);
      },
      { 
        enableHighAccuracy: false, 
        timeout: 7000, 
        maximumAge: 0 
      }
    );
  }, [set, t, i18n.language]);

  // Gérer le changement de distance
  const handleDistanceChange = (event) => {
    const newDistance = parseInt(event.target.value, 10);
    setMaxDistance(newDistance);
    set('maxDistance', newDistance);
  };

  // Gérer le toggle d'une activité
  const handleActivityToggle = (categoryId) => {
    setSelectedActivities(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      }
      
      if (prev.length >= MAX_ACTIVITIES) {
        setSaveMessage({
          type: 'info',
          text: t('pages.feed.maxActivitiesReached', { count: MAX_ACTIVITIES })
        });
        setTimeout(() => setSaveMessage(null), 3000);
        return prev;
      }
      
      return [...prev, categoryId];
    });
  };

  // Sauvegarder les catégories
  const handleSaveActivities = async () => {
    try {
      setIsUpdating(true);
      setSaveMessage(null);
      
      if (useCases?.updateUserProfile) {
        await useCases.updateUserProfile.execute(user.uid, {
          interests: selectedActivities
        });
      }
      
      if (setUser && user) {
        const updatedUser = {
          ...user,
          interests: selectedActivities
        };
        setUser(updatedUser);
      }
      
      setSaveMessage({
        type: 'success',
        text: t('pages.feed.saveSuccess')
      });
      
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      
      // Sauvegarder localement en cas d'erreur
      if (setUser && user) {
        setUser({ ...user, interests: selectedActivities });
        setSaveMessage({
          type: 'success',
          text: t('pages.feed.saveSuccess')
        });
        setTimeout(() => setSaveMessage(null), 3000);
      }
    } finally {
      setIsUpdating(false);
    }
  };

  if (categoriesLoading || authLoading || preferencesLoading) {
    return <div className="preferences-loading">{t('common:loading')}</div>;
  }

  const selectedCount = selectedActivities.length;

  return (
    <div className="feed-preferences-page">
      {!hideHeader && (
        <header className="preferences-header">
          <BackButton onClick={handleBack} />
          <h1 className="preferences-title">{t('pages.feed.title')}</h1>
        </header>
      )}
      
      <div className="preferences-content">
        {/* Section Localisation */}
        <section className="feed-section">
          <h2 className="feed-section-title">{t('pages.feed.location.title')}</h2>
          <p className="feed-section-description">{t('pages.feed.location.description')}</p>
          
          <div className="location-section-content">
            <div className="location-status-row">
              <div className={`location-status ${currentPosition ? 'defined' : 'not-defined'}`}>
                <span className="location-status-icon" aria-hidden="true">
                  {currentPosition ? '📍' : '○'}
                </span>
                <span className="location-status-text">
                  {currentPosition 
                    ? t('pages.feed.location.defined')
                    : t('pages.feed.location.notDefined')
                  }
                </span>
              </div>
              {getLocationDisplay() && (
                <div className="location-display">
                  {getLocationDisplay()}
                </div>
              )}
            </div>
            
            {locationMessage && (
              <div className={`location-message ${locationStatus}`} role="status" aria-live="polite">
                {locationMessage}
              </div>
            )}
            
            <button 
              className="location-update-button"
              onClick={handleUpdateLocation}
              disabled={locationStatus === LOCATION_STATUS.LOADING}
              aria-busy={locationStatus === LOCATION_STATUS.LOADING}
            >
              {locationStatus === LOCATION_STATUS.LOADING 
                ? t('pages.feed.location.updating')
                : t('pages.feed.location.updateButton')
              }
            </button>
          </div>
        </section>

        {/* Section Distance */}
        <section className="feed-section">
          <h2 className="feed-section-title">{t('pages.feed.distance.title')}</h2>
          <p className="feed-section-description">{t('pages.feed.distance.description')}</p>
          
          <div className="distance-slider-container">
            <input
              type="range"
              min="1"
              max={GEO.MAX_RADIUS_KM}
              value={Math.min(maxDistance, GEO.MAX_RADIUS_KM)}
              onChange={handleDistanceChange}
              className="distance-slider"
            />
            <div className="distance-value">
              {maxDistance} {t('pages.feed.distance.unit')}
            </div>
          </div>
        </section>

        {/* Section Catégories */}
        <section className="feed-section">
          <h2 className="feed-section-title">{t('pages.feed.categories.title')}</h2>
          <p className="feed-section-description">{t('pages.feed.categories.description')}</p>
          
          <div className="categories-selection-info">
            {selectedCount > 0 
              ? `${selectedCount}/${MAX_ACTIVITIES} ${t('pages.feed.categories.selected')}`
              : t('pages.feed.categories.none')
            }
          </div>
          
          {saveMessage && (
            <div className={`save-message ${saveMessage.type}`}>
              {saveMessage.text}
            </div>
          )}
          
          <div className="categories-grid">
            {categories?.map((category) => (
              <div 
                key={category.id} 
                className={`category-item ${selectedActivities.includes(category.id) ? 'selected' : ''} ${selectedActivities.length >= MAX_ACTIVITIES && !selectedActivities.includes(category.id) ? 'disabled' : ''}`}
                onClick={() => handleActivityToggle(category.id)}
              >
                <div className="category-item-icon">{category.icon}</div>
                <div className="category-item-label">
                  {category.labels[i18n.language] || category.labels.en}
                </div>
                {selectedActivities.includes(category.id) && (
                  <div className="category-item-check">✓</div>
                )}
              </div>
            ))}
          </div>
          
          <button 
            className="save-button"
            onClick={handleSaveActivities}
            disabled={isUpdating}
          >
            {isUpdating ? t('common:loading') : t('common:actions.save')}
          </button>
        </section>
      </div>
    </div>
  );
};

export default FeedPreferencesPage;

